// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { NotificationService } from '@way/theme-host/services/notification.service';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { PackageService } from '@way/theme-host/services/package.service';
import { Package } from '@way/theme-host/shared/types';
import {
  DarkPageHeaderComponent as PageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { DarkPageFooterComponent as PageFooterComponent } from '../shared/footer/footer.component';
import { SharedBookAppointmentModalComponent } from '@way/website-themes/themes/shared/book-appointment-modal/book-appointment-modal.component';
import { LocationService } from '@way/theme-host/services/location.service';
import { NotificationService } from '../../../../app/services/notification.service'; // NotificationService;
import { PACKAGE_ICON_FALLBACK } from '@way/theme-host/shared/constants/website-builder-assets';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'express-details'
    | 'packages'
    | 'coupons'
    | 'fleets';
  content: any;
  selected?: boolean;
}

@Component({
  selector: 'app-dark-express-auto-details-preview',
  standalone: true,
  providers: [NotificationService],
  imports: [
    CommonModule,
    PageHeaderComponent,
    PageFooterComponent,
    SharedBookAppointmentModalComponent,
  ],
  templateUrl: './express-auto-details-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./express-auto-details-preview.component.scss'],
})
export class DarkExpressAutoDetailsPreviewComponent implements OnInit, OnChanges {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {}; // Section visibility control
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  packages: Package[] = [];
  loading = false;
  error: string | null = null;
  readonly shimmerSlots = [0, 1, 2] as const;
  selectedTabs = new Map<string, 'single' | 'unlimited'>();

  // Page data from JSON
  headerData: any = null;
  pageTitleData: any = null;
  servicesSectionData: any = null;
  footerData: any = null;

  // Book Appointment Modal
  showBookAppointmentModal = false;
  isSubmittingAppointment = false;
  appointmentSuccessMessage = '';
  appointmentErrorMessage = '';
  serviceOptions: { value: string; label: string }[] = [];
  locations: any[] = [];

  constructor(
    private logger: LoggerService,
    private packageService: PackageService,
    private http: HttpClient,
    private locationService: LocationService,
    private messageService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadPageContent();
    this.loadPackages();
  }

  /**
   * Responds to Angular input changes by reloading page content, updating header navigation, and refreshing section data when relevant properties change.
   * @example
   * gOnChanges({ pageData: { currentValue: { /* ... */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData'] && !changes['pageData'].firstChange) {
      this.loadPageContent();
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
    // Reload page content when elements change (for background color updates)
    if (changes['elements'] && !changes['elements'].firstChange) {
      // Force reload of pageTitleData and servicesSectionData to get updated background color
      const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
      const sections = page?.sections || {};
      this.pageTitleData = sections['page-title'] || {};
      this.servicesSectionData = sections.services_section || {};
    }
  }

  /**
   * Loads page content for the Express Auto Details preview component by extracting sections from the component's pageData (handling both editor and preview formats), assigns header, page title, services and footer section data to component fields, and updates header navigation.
   * @example
   * loadPageContent()
   * undefined
   * @returns {void} Does not return a value; updates component state (headerData, pageTitleData, servicesSectionData, footerData) and triggers updateHeaderNavigation().
   **/
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('? Loading Express Auto Details page content:', this.pageData);

    // Extract sections from page data - handle both editor and preview formats
    // Editor format: pageData.page.sections
    // Preview format: pageData.json_data.page.sections
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const sections = page?.sections || {};

    this.headerData = sections.header || {};
    this.pageTitleData = sections['page-title'] || {};
    this.servicesSectionData = sections.services_section || {};
    this.footerData = sections.footer || {};

    this.logger.debug('Express Auto Details sections loaded:', {
      header: this.headerData,
      pageTitle: this.pageTitleData,
      services: this.servicesSectionData,
      footer: this.footerData,
    });

    // Update header navigation to match current pages
    this.updateHeaderNavigation();
  }

  /**
   * Update header navigation based on this.pages and ensure a default CTA button exists.
   * @example
   * component.updateHeaderNavigation()
   * undefined
   * @returns {void} Updates this.headerData.navigation (derived from active pages) and ensures this.headerData.ctaButton exists with default values when missing.
   */
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug(
        'Updated header navigation for Express Auto Details:',
        this.headerData.navigation
      );
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Load detailing packages from the PackageService and update component state (packages, loading, error).
   * @example
   * loadPackages()
   * undefined
   * @returns {void} No return value; updates component state with loaded packages or sets an error message.
   **/
  private loadPackages(): void {
    this.loading = true;
    this.error = null;

    this.packageService.getDetailingPackages().subscribe({
      next: response => {
        this.logger.debug('? Detailing packages loaded:', response);
        if (response.status && response.data) {
          this.packages = response.data;
          this.packages.forEach(pkg => {
            if (this.hasSingleWash(pkg)) {
              this.selectedTabs.set(pkg.id, 'single');
            } else if (this.hasUnlimitedWash(pkg)) {
              this.selectedTabs.set(pkg.id, 'unlimited');
            }
          });
          this.logger.debug('Packages count:', this.packages.length);
        } else {
          this.error = 'No detailing packages available';
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('? Error loading detailing packages:', err);
        this.error = 'Failed to load detailing packages';
        this.loading = false;
      },
    });
  }

  // Format price for display
  formatPrice(price: number | string | undefined): string {
    if (!price) return '$0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(2)}`;
  }

  // Get package image or fallback
  getPackageImage(pkg: Package): string {
    // Use image from API if available, otherwise fall back to static placeholder
    if (pkg.image && pkg.image.trim() !== '') {
      return pkg.image;
    }
    return PACKAGE_ICON_FALLBACK;
  }

  // Get package image fit/scaletype
  /**
   * Determines the CSS object-fit value for a package image based on the package's scaletype configuration.
   * @example
   * etPackageImageFit({ id: 'pkg1' })
   * 'cover'
   * @param {{Package}} {{pkg}} - Package object whose id is used to look up scaletype in servicesSectionData.
   * @returns {{string}} Returns a CSS object-fit value ('fill', 'cover', 'contain', or 'scale-down'), defaults to 'cover'.
   **/
  getPackageImageFit(pkg: Package): string {
    // Check services_section.packages array for this package's scaletype
    const servicesSectionPackages = this.servicesSectionData?.packages || [];
    const packageConfig = servicesSectionPackages.find((p: any) => p.id === pkg.id);

    if (packageConfig?.scaletype) {
      // Map API scaletype to CSS object-fit values
      const scaletype = packageConfig.scaletype;
      if (scaletype === 'fill' || scaletype === 'stretch') {
        return 'fill';
      } else if (scaletype === 'cover') {
        return 'cover';
      } else if (scaletype === 'contain') {
        return 'contain';
      } else if (scaletype === 'fit') {
        return 'scale-down';
      }
    }

    // Default to cover if not specified
    return 'cover';
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = PACKAGE_ICON_FALLBACK;
  }

  // Get display price based on selected tab
  getListPrice(pkg: Package): string {
    const tab = this.getSelectedTab(pkg);
    return tab === 'unlimited' ? pkg.monthly_list_price : pkg.single_list_price;
  }

  getStrikeoffPrice(pkg: Package): string {
    const tab = this.getSelectedTab(pkg);
    return tab === 'unlimited' ? pkg.monthly_strikeoff_price : pkg.single_strikeoff_price;
  }

  hasSingleWash(pkg: Package): boolean {
    return !!(
      pkg.wash_types &&
      pkg.wash_types.includes('single_wash') &&
      pkg.single_list_price &&
      parseFloat(pkg.single_list_price) > 0
    );
  }

  hasUnlimitedWash(pkg: Package): boolean {
    return !!(
      pkg.wash_types &&
      pkg.wash_types.includes('monthly_membership') &&
      pkg.monthly_list_price &&
      parseFloat(pkg.monthly_list_price) > 0
    );
  }

  getSelectedTab(pkg: Package): 'single' | 'unlimited' {
    const selected = this.selectedTabs.get(pkg.id);
    if (selected) return selected;
    return this.hasSingleWash(pkg) ? 'single' : 'unlimited';
  }

  setSelectedTab(pkg: Package, tab: 'single' | 'unlimited'): void {
    this.selectedTabs.set(pkg.id, tab);
    this.cdr.detectChanges();
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    return this.sectionVisibility[sectionKey] !== false;
  }

  /**
   * Returns the header logo object from the component's header content, applying defaults when logo data is missing.
   * @example
   * etHeaderLogo()
   * { image: 'https://example.com/logo.png', text: 'Logo', link: '/' }
   * @returns {Logo} Logo object with image, text and link properties.
   **/
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.logo) {
      return {
        image: headerContent.logo.image || headerContent.logo.url || '',
        text: headerContent.logo.text || 'Logo',
        link: headerContent.logo.link || '/',
      };
    }
    return { text: 'Logo', link: '/' };
  }

  /**
   * Build and return the header navigation items for the preview component.
   * @example
   * etHeaderNavigation()
   * [{ label: 'Home', link: '#home', active: true }]
   * @returns {NavigationItem[]} Array of navigation items derived from header content or visible pages; returns an empty array if none are available.
   **/
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      return headerContent.navigation;
    }
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Retrieve the header CTA button configuration from the component's header content, falling back to default CTA values.
   * @example
   * etHeaderCTAButton()
   * { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @returns {{CTAButton | null}} A CTAButton object containing text, link, backgroundColor, and textColor, or null if unavailable.
   **/
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.ctaButton) {
      return {
        text: headerContent.ctaButton.text || headerContent.ctaButton.label || 'Book Wash',
        link: headerContent.ctaButton.link || '#',
        backgroundColor: headerContent.ctaButton.backgroundColor || '#1B5BFF',
        textColor: headerContent.ctaButton.textColor || '#FFFFFF',
      };
    }
    return {
      text: 'Book Wash',
      link: '#',
      backgroundColor: '#1B5BFF',
      textColor: '#FFFFFF',
    };
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.backgroundColor) {
      return headerContent.backgroundColor as string;
    }
    return null;
  }

  /**
   * Returns footer quick links from footer content or generates them from visible pages.
   * @example
   * etFooterQuickLinks()
   * [{ label: 'Contact', link: '#contact' }, { label: 'About Us', link: '#about-us' }]
   * @returns {{Array<{ label: string; link: string }>}} Array of quick link objects with label and link.
   **/
  getFooterQuickLinks(): { label: string; link: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Extracts and normalizes social links from the component's footer content.
   * @example
   * etFooterSocialLinks()
   * [{ platform: 'Facebook', url: 'https://facebook.com/example', icon: 'facebook' }, { platform: 'Twitter', url: 'https://twitter.com/example', icon: 'twitter' }]
   * @param {void} none - This function does not accept any arguments.
   * @returns {Array<{ platform: string; url: string; icon: string }>} Array of normalized social link objects.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }
    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.socialLinksTitle || 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.quickLinksTitle || 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.copyright) {
      return footerContent.copyright;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Retrieve the footer logo object from the component, falling back to the header logo or a default.
   * @example
   * etFooterLogo()
   * { text: 'way', image: 'logo.png' }
   * @param {{void}} {{}} - No arguments.
   * @returns {{text?: string; image?: string} | undefined} Returns an object containing optional text and image properties, or undefined.
   **/
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#1B1B1B';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  onHeaderClick(): void {
    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#FFFFFF';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#D3D5D9';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Plus Jakarta Sans';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Open book appointment modal
   */
  onBookAppointment(): void {
    this.showBookAppointmentModal = true;
    this.appointmentSuccessMessage = '';
    this.appointmentErrorMessage = '';
    this.loadServiceOptions();
    this.loadLocations();
  }

  /**
   * Close book appointment modal
   */
  onCloseBookAppointmentModal(): void {
    this.showBookAppointmentModal = false;
    this.isSubmittingAppointment = false;
    this.appointmentSuccessMessage = '';
    this.appointmentErrorMessage = '';
  }

  /**
   * Load service options from packages
   */
  private loadServiceOptions(): void {
    this.serviceOptions = this.packages.map(pkg => ({
      value: pkg.id,
      label: `${pkg.package_name} - $${parseFloat(pkg.single_list_price || '0').toFixed(2)}`,
    }));
  }

  /**
   * Check if we're in website preview mode
   */
  private isWebsitePreviewMode(): boolean {
    try {
      return window.location.pathname.includes('/preview');
    } catch (error) {
      return false;
    }
  }

  /**
   * Load locations for the modal
   */
  private loadLocations(): void {
    if (this.locations.length > 0) return;

    this.locationService.getLocations().subscribe({
      next: (response: any) => {
        if (response?.status && response?.data) {
          this.locations = response.data
            .filter((loc: any) => loc.status === true || loc.status === 'active')
            .map((loc: any) => ({
              id: loc.id,
              name: loc.branch || loc.name,
              address:
                `${loc.street_address || ''}, ${loc.city || ''}, ${loc.state || ''} ${loc.zip_code || ''}`.trim(),
            }));
        }
      },
      error: err => this.logger.error('Error loading locations:', err),
    });
  }

  /**
   * Handle schedule appointment submission
   */
  onScheduleAppointment(formData: any): void {
    // Check if we're in preview mode
    if (this.isWebsitePreviewMode()) {
      this.messageService.add({
        severity: 'info',
        summary: 'Preview Mode',
        detail: 'This feature is not available in website preview.',
        life: 5000,
      });
      return;
    }

    // For preview components, show a message since appointments require proper setup
    this.messageService.add({
      severity: 'info',
      summary: 'Preview Mode',
      detail:
        'Appointment booking is not available in preview. This feature works on the published website.',
      life: 5000,
    });
    this.onCloseBookAppointmentModal();
  }

  /**
   * Get page title text
   */
  getPageTitle(): string {
    if (this.pageTitleData?.title) {
      if (typeof this.pageTitleData.title === 'string') {
        return this.pageTitleData.title;
      } else if (this.pageTitleData.title?.label) {
        return this.pageTitleData.title.label;
      }
    }
    return 'Express Auto Details – Shine in No Time!';
  }

  /**
   * Get page subtitle/description text
   */
  getPageSubtitle(): string {
    if (this.pageTitleData?.description) {
      return this.pageTitleData.description;
    } else if (this.pageTitleData?.subtitle) {
      return this.pageTitleData.subtitle;
    }
    return "Whether it's a quick clean or a full headlamp refresh, our Express Detail Packages give your ride that 'like-new' look without the wait.";
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    // Check page-level title first (highest priority)
    const page = this.pageData?.page || this.pageData;
    if (page?.title && typeof page.title === 'object' && page.title.style?.text_style) {
      const titleStyleObj = page.title.style.text_style;
      const styles: any = {};
      if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
      if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
      if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
      if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
      return styles;
    }

    // Fallback to section-level title
    if (this.pageTitleData?.title && typeof this.pageTitleData.title === 'object') {
      const title = this.pageTitleData.title;
      const titleStyleObj = title.style?.text_style;

      if (titleStyleObj && typeof titleStyleObj === 'object') {
        const styles: any = {};
        if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
        if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
        if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
        if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
        return styles;
      }
    }

    return {};
  }

  /**
   * Get page header section element for selection
   */
  getPageHeaderSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'express-details' ||
        el.id === 'express-details' ||
        (el as any).originalKey === 'page-title'
    );
  }

  /**
   * Get page header section background color
   */
  getPageHeaderSectionBackgroundColor(): string | null {
    // Priority 1: Check element content (most up-to-date, updated immediately)
    const pageHeaderElement = this.getPageHeaderSectionElement();
    const pageHeaderContent = pageHeaderElement?.content as any;
    if (pageHeaderContent?.backgroundColor) {
      return pageHeaderContent.backgroundColor;
    }
    // Priority 2: Check pageTitleData (from API/pageData)
    if (this.pageTitleData?.backgroundColor) {
      return this.pageTitleData.backgroundColor;
    }
    // Priority 3: Check servicesSectionData (from API/pageData)
    if (this.servicesSectionData?.backgroundColor) {
      return this.servicesSectionData.backgroundColor;
    }
    // Priority 4: Check page-level theme background color
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.theme?.background_color) {
      return page.theme.background_color;
    }
    // Priority 5: Check pageData directly
    const sections = page?.sections || {};
    const pageTitleSection = sections['page-title'] || {};
    if (pageTitleSection.backgroundColor) {
      return pageTitleSection.backgroundColor;
    }
    const servicesSection = sections.services_section || {};
    if (servicesSection.backgroundColor) {
      return servicesSection.backgroundColor;
    }
    // Default fallback
    return null;
  }

  /**
   * Get services section background color (same as page header for unified look)
   */
  getServicesSectionBackgroundColor(): string | null {
    return this.getPageHeaderSectionBackgroundColor();
  }

  /**
   * Handle page header section click
   */
  onPageHeaderSectionClick(event: Event): void {
    event.stopPropagation();
    const pageHeaderElement: WebsiteElement = {
      id: 'express-details',
      type: 'express-details',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        backgroundColor: this.getPageHeaderSectionBackgroundColor(),
      },
      selected: true,
      originalKey: 'page-title',
    } as any;
    this.logger.debug('✨ Page header section clicked, emitting element:', pageHeaderElement);
    this.elementSelect.emit(pageHeaderElement);
  }

  /**
   * Handle services section click
   */
  onServicesSectionClick(event: Event): void {
    event.stopPropagation();
    const servicesElement: WebsiteElement = {
      id: 'services',
      type: 'services',
      content: {
        ...(this.servicesSectionData || {}),
        backgroundColor: this.getServicesSectionBackgroundColor(),
      },
      selected: true,
      originalKey: 'services_section',
    } as any;
    this.elementSelect.emit(servicesElement);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
